import { type ReactNode, useMemo } from 'react';
import React from 'react';
import { ERROR_TYPE, PILLARS, POSTING_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import type { ContentLexicalStateV1 } from '@soundxyz/utils/lexical/ContentLexicalStateV1';
import { CommentButton } from '@components/Feed/Post/CommentButton';
import { UpvoteButton } from '@components/Feed/Post/UpvoteButton';
import { LexicalDisplay } from '@components/Lexical/Display';
import { Avatar } from '@components/basics/Images/Avatar';
import { Timestamp } from '@components/basics/Timestamp';
import { ReplyPostCommentModal } from '@components/modals/ReplyModal/ReplyCommentModal';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { ModalType, useModal } from '@context/modal';
import {
  CommentAuthorFragmentDoc,
  CurrentUserPostVoteStatus,
  type FragmentType,
  getFragment,
  gql,
  PostVoteChange,
  useMutation,
  useQuery,
  UserVoteOnPostCommentDocument,
  VoteOnPostCommentDocument,
} from '@graphql/reactQuery';
import { useIsHydrated } from '@hooks/useIsHydrated';
import { useLogError } from '@hooks/useLogErrorNew';
import { useStableCallback } from '@hooks/useStableCallback';
import { webappUriPath } from '@utils/url';
import { AccessibleLink } from '../link/AccessibleLink';
import { FeedCuratorTooltip } from '../tooltip/FeedCuratorTooltip';
import { VerifiedTooltip } from '../tooltip/VerifiedTooltip';
import { setVoteStatus, useBatchedKey } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CommentAuthor on User {
    id
    username
    webappUri
    publicAddress
    avatar {
      id
      url
    }
    artist {
      id
    }
  }

  mutation VoteOnPostComment($input: SetVoteOnPostCommentInput!) {
    voteOnPostComment(input: $input) {
      __typename
      ... on MutationVoteOnPostCommentSuccess {
        data
      }
      ... on NotFoundError {
        message
      }
      ... on RateLimitError {
        message
      }
    }
  }

  query UserVoteOnPostComment($input: QueryUserVoteOnPostCommentInput!) {
    userVoteOnPostComment(input: $input) {
      postCommentId
      voteStatus
      upvoteCount
    }
  }
`);

type UserCommentItemProps = {
  action: ReactNode;
  createdAt: string;
  selectedSlotInfo: {
    timestamp: string;
    onClick: () => void;
  } | null;
  content: string | ContentLexicalStateV1;
  postCommentId: string;
  showArtistTag: boolean;
  showCollectorTag: boolean;
  showFeedCuratorBadge: boolean;
  showVerifiedBadge: boolean;
  upvoteCount: number;
  userFragment: FragmentType<CommentAuthorFragmentDoc>;
};

export const UserCommentItem = ({
  action,
  createdAt,
  selectedSlotInfo,
  content,
  postCommentId,
  showArtistTag,
  showCollectorTag,
  showFeedCuratorBadge,
  showVerifiedBadge,
  upvoteCount,
  userFragment,
}: UserCommentItemProps) => {
  const { isAuthenticatedWithAPI } = useAuth();

  const { openModal } = useModal();

  const user = getFragment(CommentAuthorFragmentDoc, userFragment);
  const { username, webappUri, publicAddress, avatar } = user;
  const userWebappUri = webappUriPath(webappUri);

  const postCommentIds = useBatchedKey({ key: postCommentId });

  const hydrated = useIsHydrated();

  const logError = useLogError();

  const showTag = showArtistTag || showCollectorTag;

  const { data: voteData } = useQuery(UserVoteOnPostCommentDocument, {
    variables: !!postCommentIds && {
      input: { postCommentIds },
    },
    cacheTime: Infinity,
    select({ data }) {
      for (const statusData of data.userVoteOnPostComment) {
        if (statusData.postCommentId === postCommentId)
          return { voteStatus: statusData.voteStatus, upvoteCount: statusData.upvoteCount };
      }
      return;
    },
    enabled: isAuthenticatedWithAPI && !!postCommentIds?.length,
    keepPreviousData: true,
    staleTime: 0,
  });

  const { mutateAsync, isLoading: isVoting } = useMutation(VoteOnPostCommentDocument, {
    retry: 3,
  });

  const handleReplyClicked = useStableCallback(() => {
    if (!isAuthenticatedWithAPI) {
      triggerLogin();
      return;
    }

    openModal(ModalType.REPLY_POST, {
      body: <ReplyPostCommentModal postCommentId={postCommentId} />,
    });
  });

  const handleUpvoteClicked = useStableCallback(async () => {
    if (!isAuthenticatedWithAPI) {
      triggerLogin();
      return;
    }

    if (isVoting || !voteData) return;

    const { voteStatus, upvoteCount } = voteData;

    const isUpvoting = voteStatus === CurrentUserPostVoteStatus.NoVote;

    setVoteStatus({
      postCommentId,
      voteStatus: isUpvoting ? CurrentUserPostVoteStatus.Upvoted : CurrentUserPostVoteStatus.NoVote,
      upvoteCount: isUpvoting ? upvoteCount + 1 : upvoteCount - 1,
    });

    try {
      const result = await mutateAsync({
        input: {
          postCommentId,
          voteStatus: isUpvoting ? PostVoteChange.Upvote : PostVoteChange.RemoveVote,
        },
      });

      if (result.data.voteOnPostComment?.__typename === 'NotFoundError') {
        logError({
          action: POSTING_ERROR_ACTIONS.UPVOTING_POST_COMMENT_FAILED,
          error: new Error(result.data.voteOnPostComment.message),
          errorType: ERROR_TYPE.UPVOTING_ERROR,
          isArtistOnlyPage: false,
          level: 'error',
          message: result.data.voteOnPostComment.message,
          toast: 'The comment you are trying to upvote does not exist.',
          indexedTags: {
            postCommentId,
            upvoteCount,
            voteStatus,
          },
          pillar: PILLARS.POSTING,
        });
      }

      if (result.data.voteOnPostComment?.__typename === 'RateLimitError') {
        logError({
          action: POSTING_ERROR_ACTIONS.UPVOTING_POST_COMMENT_FAILED,
          error: new Error(result.data.voteOnPostComment.message),
          errorType: ERROR_TYPE.UPVOTING_ERROR,
          isArtistOnlyPage: false,
          level: 'error',
          message: result.data.voteOnPostComment.message,
          toast: 'You are upvoting too fast, try again later please.',
          indexedTags: {
            postCommentId,
            upvoteCount,
            voteStatus,
          },
          pillar: PILLARS.POSTING,
        });
      }
    } catch (error) {
      logError({
        action: POSTING_ERROR_ACTIONS.UPVOTING_POST_COMMENT_FAILED,
        error,
        errorType: ERROR_TYPE.UPVOTING_ERROR,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'Error upvoting a post comment',
        indexedTags: {
          postCommentId,
          upvoteCount,
          voteStatus,
        },
        pillar: PILLARS.POSTING,
      });
    }
  });

  const upvoteCountMemoized = useMemo(() => {
    if (isAuthenticatedWithAPI && voteData) {
      return voteData.upvoteCount;
    } else {
      return upvoteCount;
    }
  }, [isAuthenticatedWithAPI, voteData, upvoteCount]);

  const isLoading = useMemo(() => {
    if (isAuthenticatedWithAPI) {
      return !Boolean(voteData);
    } else {
      return !hydrated;
    }
  }, [hydrated, isAuthenticatedWithAPI, voteData]);

  return (
    <div className="py-1.5">
      <div className="min-h-10 flex justify-between rounded-md">
        <div className="flex w-full gap-2">
          <div className="shrink-0">
            <Avatar
              size={40}
              src={avatar?.url}
              walletAddress={publicAddress}
              username={username}
              borderRadius={1000}
              webappUri={userWebappUri}
            />
          </div>
          <div className="flex w-full min-w-0 flex-col gap-1">
            <div className="flex w-full min-w-0 justify-between">
              <div className="flex w-full min-w-0 max-w-[70%] items-center gap-2">
                <div className="flex items-center gap-2 truncate text-base-s font-semibold text-base900">
                  <div className="flex min-w-0 items-center gap-0.5">
                    <AccessibleLink
                      pathnameUri={userWebappUri}
                      className="truncate font-base text-base-m font-semibold text-base900"
                    >
                      {username}
                    </AccessibleLink>
                    {showVerifiedBadge && <VerifiedTooltip size={14} />}
                    {showFeedCuratorBadge && <FeedCuratorTooltip portal size={14} />}
                  </div>
                  {showTag && (
                    <div className="flex-shrink-0 rounded-sm bg-base300 px-1 py-[2px] font-base text-base-xs font-normal text-base500">
                      {showArtistTag ? 'Artist' : 'Collector'}
                    </div>
                  )}
                  {selectedSlotInfo != null && (
                    <span
                      className="relative flex flex-shrink-0 cursor-pointer truncate whitespace-nowrap font-base text-base-xs font-normal tabular-nums text-base500 duration-300 hover:text-base600"
                      onClick={selectedSlotInfo.onClick}
                    >
                      {selectedSlotInfo.timestamp}
                    </span>
                  )}
                  <Timestamp
                    timeStamp={createdAt}
                    className="font-base !text-base-xs font-normal text-base500 duration-300 hover:text-base600"
                  />
                </div>
              </div>
              <div className="relative flex items-center gap-1">
                <div className="flex h-4 w-8">{action}</div>
              </div>
            </div>

            {typeof content === 'object' ? (
              <LexicalDisplay state={content} />
            ) : (
              <div className="break-words font-base text-base-m text-base900">{content}</div>
            )}

            {isLoading ? (
              <div className="mt-2 w-10 animate-pulse rounded-md bg-neutral200" />
            ) : (
              <div className="mt-2 flex h-7 items-center gap-7">
                <div className="-mx-2 -my-1">
                  <UpvoteButton
                    disabled={false}
                    upvoteCount={upvoteCountMemoized}
                    voteStatus={voteData?.voteStatus}
                    onClick={handleUpvoteClicked}
                    iconSize={14}
                  />
                </div>
                <div className="-mx-2 -my-1">
                  <CommentButton onClick={handleReplyClicked} variant="comment" iconSize={14} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const UserCommentItemSkeleton = () => (
  <div className="flex animate-pulse gap-3">
    <div className="h-10 w-10 rounded-full bg-base300" />
    <div className="flex flex-col gap-1">
      <div className="flex gap-2">
        <div className="h-4 w-20 rounded-lg bg-base300" />
        <div className="h-4 w-40 rounded-lg bg-base300" />
      </div>
      <div className="h-4 w-40 rounded-lg bg-base300" />
    </div>
  </div>
);
